import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentNotice, getOldNotices } from '../api/notices.api';
import { Notice } from '../types/notice';
import LoadingSpinner from '../components/LoadingSpinner';
import NoticeMarquee from '../components/NoticeMarquee';
import NoticeList from '../components/NoticeList';
import NoticeDetailModal from '../components/NoticeDetailModal';

const HomePage: React.FC = () => {
  const [currentNotice, setCurrentNotice] = useState<Notice | null>(null);
  const [oldNotices, setOldNotices] = useState<Notice[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedNotice, setSelectedNotice] = useState<Notice | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchCurrentNotice = async () => {
      try {
        const currentNoticeResponse = await getCurrentNotice();
        setCurrentNotice(currentNoticeResponse.data);
      } catch (err) {
        // Silent fail - notices are optional
        setCurrentNotice(null);
      }
    };

    fetchCurrentNotice();
  }, []);

  useEffect(() => {
    const fetchOldNotices = async () => {
      setLoading(true);
      try {
        console.log('HomePage - Fetching old notices, page:', currentPage);
        const response = await getOldNotices(currentPage, 6);
        console.log('HomePage - getOldNotices response:', response);
        console.log('HomePage - response.data:', response?.data);
        console.log('HomePage - response.data.notices:', response?.data?.notices);
        
        if (response && response.data) {
          if (response.data.notices && Array.isArray(response.data.notices)) {
            console.log('HomePage - Setting old notices:', response.data.notices.length, 'notices');
          setOldNotices(response.data.notices);
            setTotalPages(response.data.totalPages || 1);
          } else {
            console.warn('HomePage - Unexpected response structure:', response.data);
            console.warn('HomePage - Response keys:', Object.keys(response.data || {}));
            setOldNotices([]);
        }
        } else {
          console.warn('HomePage - No response data');
          console.warn('HomePage - Full response:', response);
          setOldNotices([]);
        }
      } catch (err: any) {
        console.error('HomePage - Error fetching old notices:', err);
        console.error('HomePage - Error status:', err.response?.status);
        console.error('HomePage - Error details:', err.response?.data);
        console.error('HomePage - Error message:', err.message);
        
        // Don't set empty array on 401/403 - might be auth issue
        if (err.response?.status === 401 || err.response?.status === 403) {
          console.warn('HomePage - Authentication issue, notices may require login');
        }
        setOldNotices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOldNotices();
  }, [currentPage]);


  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      {/* Current Notice Marquee - Right after navbar */}
      {currentNotice && (
        <div className="mb-3">
          <NoticeMarquee 
            notice={currentNotice}
            onClick={() => {
              setSelectedNotice(currentNotice);
              setShowModal(true);
            }}
          />
        </div>
      )}
      <div className="home-shell p-3 p-md-4 p-lg-5 rounded-4">
        <section className="home-hero rounded-4 p-3 p-md-4 p-lg-5 mb-4">
          <div className="row align-items-center g-4">
            <div className="col-lg-7">
              <p className="text-uppercase text-muted small mb-2 d-none d-md-block">INTER DISTRICT NATIONAL TAPE TENNIS CHAMPIONSHIP 2026</p>
              <h1 className="display-6 fw-bold mb-3" style={{ fontSize: 'clamp(1.25rem, 4vw, 2.5rem)' }}>
                INTER DISTRICT NATIONAL TAPE TENNIS CHAMPIONSHIP 2026
              </h1>
              <p className="fw-semibold text-muted mb-3" style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', lineHeight: '1.6' }}>
                তোমার নিজ জেলার ক্রিকেট টিমের প্রতিনিধিত্ব করতে রেজিষ্ট্রেশন করো এখনি।
              </p>
              <ul className="text-muted mb-4">
                <li>Over 50,000+ Players Registration!</li>
                <li>Exciting Prizes &amp; Trophies!</li>
                <li>Sign Up Today!</li>
              </ul>
              <div className="d-flex flex-column flex-sm-row gap-2">
                <Link to="/auth" className="btn btn-dark px-4">
                  Register Now
                </Link>
                <Link to="/events" className="btn btn-outline-dark px-4">
                  View Events
                </Link>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="home-hero-illustration rounded-4 d-flex align-items-center justify-content-center">
                <img
                  src="/home_page/WhatsApp%20Image%202025-12-29%20at%2018.57.35.jpeg"
                  alt="District Cricket Tournament logo"
                  className="hero-logo"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="mb-4">
          <div className="card card-surface">
            <div className="card-body p-3 p-md-4">
              <div className="ratio ratio-16x9 rounded overflow-hidden">
                <video
                  src="/home_page/WhatsApp%20Video%202025-12-29%20at%2018.57.37.mp4"
                  autoPlay
                  loop
                  playsInline
                  controls
                />
              </div>
            </div>
          </div>
        </section>

        {/* Notice Section */}
        <section className="mb-4">
          <div className="home-section-heading mb-3">
            <h5 className="home-section-title mb-1">Previous Notices</h5>
            <span className="text-muted small">Archived announcements and updates</span>
          </div>

          {/* Old Notices List */}
          {oldNotices && oldNotices.length > 0 ? (
            <div className="card card-surface">
              <div className="card-body p-4">
                <h6 className="mb-3">Previous Notices</h6>
                <NoticeList
                  notices={oldNotices}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          ) : (
            !loading && (
              <div className="card card-surface">
                <div className="card-body p-4">
                  <h6 className="mb-3">Previous Notices</h6>
                  <p className="text-muted-soft mb-0">No archived notices available.</p>
                </div>
              </div>
            )
          )}
        </section>

        <section id="services" className="mb-4">
          <div className="home-section-heading mb-3">
            <h5 className="home-section-title mb-1">Services</h5>
            <span className="text-muted small">Explore the full equipment &amp; programs</span>
          </div>
          <div className="row g-3">
            {[
              { 
                title: 'Customised Jersey', 
                image: '/customised_Jersey.jpeg',
                slug: 'customised-jersey',
                description: 'ক্লাব ও টিম জার্সি',
                options: [
                  'National Team Jersey',
                  'Club Football Jersey'
                ]
              },
              { 
                title: 'Apparel Production', 
                image: '/Apparel_production.jpeg',
                slug: 'apparel-production',
                description: 'নিজের টিমের জন্য পছন্দের জার্সি ও টি-শার্ট অর্ডার করুন।',
                options: [
                  'Customised Jersey',
                  'Trouser',
                  'Cap',
                  'Event And Corporate T-shirt',
                  'Others'
                ]
              },
              { 
                title: 'Sports Item', 
                image: '/SPORTS_ITEMS.jpeg',
                slug: 'sports-item',
                description: 'সবধরনের স্পোর্টস আইটেম হাব',
                options: [
                  'Cricket Gears',
                  'Football Accessories',
                  'Badminton',
                  'Other Games'
                ]
              },
              { 
                title: 'Award and Trophy', 
                image: '/Award_and_Trophy.jpeg',
                slug: 'award-and-trophy',
                description: 'সকল ধরনের এওয়ার্ড',
                options: [
                  'Sports Zone BD Special',
                  'Metal',
                  'Wooden',
                  'Glass',
                  'Fibre'
                ]
              },
              { 
                title: 'Hunting Program', 
                image: '/Hunting_program.jpeg',
                slug: 'hunting-program',
                description: 'দেশব্যাপী প্রতিভা অন্বেষণ',
                options: [
                  'Cricketer Hunting',
                  'Footballer Hunting',
                  'Other Games'
                ]
              },
              { 
                title: 'Sports Events', 
                image: '/Sports_Event.jpeg',
                link: '/events',
                description: 'জাতীয় ও প্রতিযোগিতামূলক স্পোর্টস ইভেন্ট',
                options: [
                  'Cricket Tournament',
                  'Football Tournament',
                  'Other Games'
                ]
              },
              { 
                title: 'Dealership Program', 
                image: '/Dealership.jpeg',
                slug: 'dealership-program',
                description: 'দেশব্যাপী তরুণ উদ্যোক্তা তৈরীর পদক্ষেপ।',
                options: [
                  'Plan For Online Business',
                  'Outlet In Your Own District'
                ]
              },
            ].map((service, index) => (
              <div className={index === 6 ? 'col-12 col-md-6 col-lg-4' : 'col-12 col-md-6 col-lg-4'} key={service.title}>
                <div className="service-card rounded-3 h-100 d-flex flex-column overflow-hidden">
                  <div className="service-card-image-container">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="service-card-image"
                      loading="lazy"
                    />
                    </div>
                  <div className="service-card-content p-3 d-flex flex-column flex-grow-1">
                    <h6 className="mb-2 text-uppercase fw-bold" style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>{service.title}</h6>
                    <p className="text-muted-soft small mb-3 flex-grow-1" style={{ fontSize: 'clamp(0.75rem, 1.8vw, 0.875rem)', lineHeight: '1.5' }}>{service.description}</p>
                      {service.link ? (
                      <Link to={service.link} className="btn btn-service-modern btn-sm w-100">
                          View More
                        <i className="bi bi-arrow-right ms-2"></i>
                        </Link>
                      ) : service.slug ? (
                      <Link to={`/services/${service.slug}`} className="btn btn-service-modern btn-sm w-100">
                          View More
                        <i className="bi bi-arrow-right ms-2"></i>
                        </Link>
                      ) : (
                      <button type="button" className="btn btn-service-modern btn-sm w-100">
                          View More
                        <i className="bi bi-arrow-right ms-2"></i>
                        </button>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Notice Detail Modal */}
        <NoticeDetailModal
          show={showModal}
          notice={selectedNotice}
          onClose={() => {
            setShowModal(false);
            setSelectedNotice(null);
          }}
        />
      </div>
    </div>
  );
};

export default HomePage;
